import { useStackChatEnabled } from "@mirage/service-experimentation/useStackChatEnabled";
import i18n from "@mirage/translations";
import styles from "./Actions.module.css";
import { ManageStackMenu } from "./ManageStackMenu";
import { PinStackButton } from "./PinStackButton";
import { ShareStackButton, ShareStackIconButton } from "./ShareStackButton";

import type { stacks, users } from "@dropbox/api-v2-client";
import type { MenuPlacement } from "@dropbox/dig-components/menu";
import { UIIcon } from "@dropbox/dig-icons";
import { Twinkle2Line } from "@dropbox/dig-icons/dist/mjs/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type { DashActionSurface } from "@mirage/analytics/events/enums/dash_action_surface";
import { PAP_Click_StackChat } from "@mirage/analytics/events/types/click_stack_chat";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import {
  type AskWithMultiDocsParams,
  CtaSource,
} from "@mirage/service-conversation/router_state_types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import { IconButtonWithTooltip } from "@mirage/shared/icons/IconButtonWithTooltip";
import { useReportStackPapEvent } from "@mirage/stacks/hooks/useReportStackPapEvent";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { useNavigate } from "react-router";
import type { ActionsMenuProps, SetMenuOpenForStackId } from "./ActionsMenu";

export type ActionsProps = Omit<ActionsMenuProps, "onShare" | "stack"> & {
  stack: stacks.Stack | null;
  currentAccount?: users.FullAccount;
  triggerEditStackRef?: React.RefObject<HTMLInputElement>;
  showShareLabeledIcon?: boolean;
  mutationRequestId?: string;
  sessionId?: string;
  chatSources?: ChatSource[];
};

export function StackSummarizeAction({
  chatSources,
  sharingId,
  stack,
  dashActionSurface = "start_page",
}: {
  chatSources: ChatSource[];
  sharingId?: string;
  stack?: stacks.Stack | null;
  dashActionSurface?: DashActionSurface;
}) {
  const navigate = useNavigate();
  const { reportPapEvent } = useMirageAnalyticsContext();
  const reportStackPapEvent = useReportStackPapEvent(stack ?? undefined);

  const handleChatClick = () => {
    if (!sharingId || !chatSources?.length) return;

    if (stack !== null) {
      reportStackPapEvent(
        PAP_Click_StackChat({
          actionSurfaceComponent: "stacks",
          dashActionSurface,
        }),
      );
    } else {
      // Note: Fallback for logging stack chat click since it's possible that we don't have the full stack sharingId which would
      // prevent us from being able to log this pap event
      reportPapEvent(
        PAP_Click_StackChat({
          actionSurfaceComponent: "stacks",
          dashActionSurface,
        }),
      );
    }

    const state = buildConversationRouterState(CtaSource.StartPageStackChat, {
      chatSources,
      scenarioMeta: {
        type: "stack",
        sharingId,
        namespaceId: stack?.namespace_id || "",
      },
    } satisfies AskWithMultiDocsParams);

    navigate(RoutePath.CHAT, {
      state,
    });
  };

  return (
    <IconButtonWithTooltip
      variant="transparent"
      disabled={!chatSources?.length}
      onClick={handleChatClick}
      aria-label={i18n.t("chat_label")}
      tooltipProps={{
        title: !chatSources?.length
          ? i18n.t("no_documents_to_summarize")
          : i18n.t("chat_label"),
      }}
    >
      <UIIcon src={Twinkle2Line} />
    </IconButtonWithTooltip>
  );
}

export default function Actions({
  stack,
  currentAccount,
  setMenuOpenForStackId,
  iconColor,
  menuPlacement,
  showPinnedIcon = true,
  triggerEditStackRef,
  showShareLabeledIcon,
  mutationRequestId,
  sessionId,
  chatSources,
}: ActionsProps) {
  const isNewStack = !stack?.namespace_id;
  const isStackChatEnabled = useStackChatEnabled() && chatSources;
  const onEditStack = () => {
    triggerEditStackRef?.current?.click();
  };
  const showShareIconOnly = !showShareLabeledIcon;

  return (
    <div className={styles.container}>
      <div className={styles.buttonContainer}>
        {showShareIconOnly && !isNewStack && (
          <ShareStackIconButton stack={stack} iconColor={iconColor} />
        )}
        {showPinnedIcon && (
          <PinStackButton
            stack={stack}
            mutationRequestId={mutationRequestId}
            sessionId={sessionId}
            currentAccount={currentAccount}
            iconColor={iconColor}
          />
        )}
        {!isNewStack && isStackChatEnabled && (
          <StackSummarizeAction
            sharingId={stack?.sharing_id}
            chatSources={chatSources ?? []}
            stack={stack}
          />
        )}
        <ManageStackMenu
          stack={stack}
          iconType="more"
          currentAccount={currentAccount}
          setMenuOpenForStackId={setMenuOpenForStackId}
          iconColor={iconColor}
          menuPlacement={menuPlacement}
          onEdit={triggerEditStackRef ? onEditStack : undefined}
        />
        {showShareLabeledIcon && !isNewStack && (
          <ShareStackButton
            stack={stack}
            iconType="arrow"
            variant="outline"
            disabled={!currentAccount}
          >
            {i18n.t("share")}
          </ShareStackButton>
        )}
      </div>
    </div>
  );
}

interface HeaderActionsProps {
  stack: stacks.Stack | null;
  menuPlacement?: MenuPlacement;
  mutationRequestId?: string;
  sessionId?: string;
  currentAccount?: users.FullAccount;
  iconColor?: string;
  setMenuOpenForStackId?: SetMenuOpenForStackId;
}

export const HeaderActions: React.FC<HeaderActionsProps> = ({
  stack,
  menuPlacement,
  mutationRequestId,
  sessionId,
  currentAccount,
  iconColor,
  setMenuOpenForStackId,
}) => {
  const isStackChatEnabled = useStackChatEnabled();

  return (
    <div className={styles.container}>
      <div className={styles.buttonContainer}>
        {!isStackChatEnabled && (
          <PinStackButton
            stack={stack}
            mutationRequestId={mutationRequestId}
            sessionId={sessionId}
            currentAccount={currentAccount}
            iconColor={iconColor}
          />
        )}
        <ManageStackMenu
          stack={stack}
          iconType="gear"
          currentAccount={currentAccount}
          setMenuOpenForStackId={setMenuOpenForStackId}
          iconColor={iconColor}
          menuPlacement={menuPlacement}
        />
      </div>
    </div>
  );
};
